import { canReadProperties, canWriteProperties, coreQueryKey } from '@/api/core/authorization';
import { readNativeAccessInventory } from '@/api/core/native-access-inventory';
import { readNativeCatalog, readNativeProperties } from '@/api/core/native-property-read';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { SearchField } from '@/components/search-field';
import { Button, ListPage, PageHeader } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { Choice } from '../property/presentation/choice';
import { PropertyFailure } from '../property/property-feedback';
import { propertyRead, propertyRuntime, type PropertyRuntime } from '../property/property-queries';
import { AccessDeviceLedger } from './access-page';
import { DevicePurposeEditor } from './device-purpose-editor';
import { DeviceUsagePanel } from './device-usage-panel';
import { SlotDoorEditor } from './slot-door-editor';

export function NativeAccessPage({ runtime = propertyRuntime }: { runtime?: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  const { propertyId } = useParams();
  const [params, setParams] = useSearchParams();
  const cursor = params.get('cursor');
  const ledger = params.get('view') === 'devices';
  const setLedger = (show: boolean) => {
    const next = new URLSearchParams(params);
    if (show) next.set('view', 'devices');
    else {
      next.delete('view');
      next.delete('device_cursor');
      next.delete('device');
    }
    setParams(next, { replace: true });
  };
  const enabled =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canReadProperties(session.authorization);
  const current = () => runtime.session.getSnapshot().generation === session.generation;
  const canEdit = enabled && !!session.authorization && canWriteProperties(session.authorization);
  const selectedSlot = params.get('slot');
  const selectedRoom = params.get('room');
  const panel = params.get('panel');
  const search = params.get('q') ?? '';
  const selectPanel = (slot: string, mode: 'links' | 'purpose') => {
    const next = new URLSearchParams(params);
    next.set('slot', slot);
    next.set('panel', mode);
    setParams(next);
  };
  const closePanel = () => {
    const next = new URLSearchParams(params);
    next.delete('slot');
    next.delete('panel');
    setParams(next);
  };
  const list = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, session.generation, 'native-properties'),
      'access',
      cursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeProperties(runtime.client, cursor, 'asc', signal),
        signal,
        current,
      ),
    enabled: enabled && !propertyId && !ledger,
    retry: false,
    meta: { suppressGlobalError: true },
  });
  const inventory = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-access-inventory',
        propertyId ?? '',
      ),
      cursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeAccessInventory(runtime.client, propertyId ?? '', cursor, signal),
        signal,
        current,
      ),
    enabled: enabled && !!propertyId && !ledger,
    retry: false,
    meta: { suppressGlobalError: true },
  });
  const catalog = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-property-catalog',
        propertyId ?? '',
      ),
      null,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeCatalog(runtime.client, propertyId ?? '', null, signal),
        signal,
        current,
      ),
    enabled: enabled && !!propertyId && !ledger,
    retry: false,
    meta: { suppressGlobalError: true },
  });
  if (!enabled) return null;
  const slotLabels = Object.fromEntries(
    (inventory.data?.core_slots ?? []).map((slot) => [
      slot.slot_id,
      { room: slot.room_name, name: slot.name },
    ]),
  );
  const visibleSlots =
    inventory.data?.core_slots.filter(
      (slot) =>
        (!selectedRoom || slot.room_id === selectedRoom) &&
        `${slot.room_name ?? ''} ${slot.name ?? ''}`
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase()),
    ) ?? [];
  const query = propertyId ? inventory : list;
  const next = propertyId ? inventory.data?.next_cursor : list.data?.next_cursor;
  const advance = (value: string | null) => {
    const p = new URLSearchParams(params);
    if (value) p.set('cursor', value);
    else p.delete('cursor');
    setParams(p);
  };
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title="출입 관리"
          description="Core 지점의 객실·Unit과 저장된 장비 연결을 확인합니다."
        />
      </ShellHeader>
      <ListPage.Body className="gap-4 p-4 sm:p-6">
        {ledger ? (
          <AccessDeviceLedger
            runtime={runtime}
            generation={session.generation}
            onBack={() => {
              setLedger(false);
            }}
          />
        ) : (
          <>
            <div className="flex flex-wrap gap-3">
              <Link className="underline" to="/access/legacy">
                기존 지점 온라인 출입 설정
              </Link>
              <Button
                variant="outline"
                onClick={() => {
                  setLedger(true);
                }}
              >
                장비 목록
              </Button>
              {propertyId && (
                <Link className="underline" to="/access">
                  지점 선택
                </Link>
              )}
            </div>
            <p className="caption text-muted-foreground">
              객실·슬롯별 문 연결과 저장된 용도를 확인하고 변경합니다.
            </p>
            {query.isFetching && !query.isPending && (
              <p role="status">출입 자료를 새로 불러오고 있습니다.</p>
            )}
            {query.isPending && <p role="status">출입 자료를 불러오고 있습니다.</p>}
            {query.isError && (
              <PropertyFailure error={query.error} onRetry={() => void query.refetch()} />
            )}
            {!propertyId && list.isSuccess && (
              <ul aria-label="출입 지점 목록" className="space-y-3">
                {list.data.data.map((p) => (
                  <li key={p.id} className="rounded-md border p-4">
                    <Link className="underline" to={`/access/native/${p.id}`}>
                      {p.name}
                    </Link>
                    <p>{p.code}</p>
                  </li>
                ))}
              </ul>
            )}
            {!propertyId && list.isSuccess && !list.data.data.length && (
              <p>조회된 지점이 없습니다.</p>
            )}
            {propertyId && (
              <>
                {catalog.isFetching && <p role="status">지점·객실 맥락을 불러오고 있습니다.</p>}
                {catalog.isError && (
                  <PropertyFailure error={catalog.error} onRetry={() => void catalog.refetch()} />
                )}
                {catalog.data && (
                  <Link className="underline" to={`/properties/${propertyId}`}>
                    {catalog.data.data.property.name} 자산 정보
                  </Link>
                )}
                <p className="body-sm text-muted-foreground">
                  저장된 연결과 활성 설정입니다. 실제 온라인 상태나 출입 허용을 뜻하지 않습니다.
                </p>
                {inventory.isSuccess && (
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="space-y-1">
                      <span className="caption">현재 페이지 객실</span>
                      <Choice
                        label="현재 페이지 객실"
                        disabled={inventory.isFetching}
                        value={selectedRoom ?? 'all'}
                        options={{
                          all: '전체 객실',
                          ...Object.fromEntries(
                            inventory.data.core_slots.map((slot) => [
                              slot.room_id,
                              slot.room_name ?? '객실명 없음',
                            ]),
                          ),
                        }}
                        onChange={(value) => {
                          const next = new URLSearchParams(params);
                          if (value === 'all') next.delete('room');
                          else next.set('room', value);
                          next.delete('slot');
                          next.delete('panel');
                          setParams(next);
                        }}
                      />
                    </label>
                    <SearchField
                      aria-label="현재 페이지 슬롯 검색"
                      value={search}
                      placeholder="객실·슬롯 이름 검색"
                      onSearch={(value) => {
                        const next = new URLSearchParams(params);
                        if (value) next.set('q', value);
                        else next.delete('q');
                        next.delete('slot');
                        next.delete('panel');
                        setParams(next);
                      }}
                    />
                  </div>
                )}
                {inventory.isSuccess && (
                  <ul aria-label="Unit별 출입 연결" className="space-y-3">
                    {visibleSlots.map((slot) => {
                      const item = inventory.data.items.find((i) => i.slot_id === slot.slot_id);
                      if (!item)
                        return (
                          <li key={slot.slot_id} role="alert">
                            연결 응답을 확인하지 못했습니다.
                          </li>
                        );
                      const room = catalog.data?.data.rooms.find((r) => r.id === slot.room_id);
                      const type = catalog.data?.data.room_types.find(
                        (t) => t.id === room?.room_type_id,
                      );
                      return (
                        <li key={slot.slot_id} className="rounded-md border p-4">
                          <h2 className="font-semibold">
                            {slot.room_name ?? '객실명 없음'} · {slot.name ?? 'Unit명 없음'}
                          </h2>
                          {type && <p>객실 유형: {type.name}</p>}
                          <Link
                            className="underline"
                            to={`/properties/${propertyId}?section=access&room=${slot.room_id}`}
                          >
                            객실 온라인 출입 설정
                          </Link>
                          <p>
                            연결:{' '}
                            {
                              {
                                CURRENT: '현재 연결',
                                NO_MAPPING: '연결 없음',
                                UNCERTAIN: '관계 확인 필요',
                                UNCONFIRMED: '장비 미확인',
                              }[item.state]
                            }
                          </p>
                          {item.device && (
                            <dl className="body-sm mt-3 grid gap-x-4 gap-y-2 sm:grid-cols-[8rem_minmax(0,1fr)]">
                              <dt className="text-muted-foreground">저장된 장비 유형</dt>
                              <dd>{item.device.device_type ?? '분류 정보 없음'}</dd>
                              <dt>활성 설정</dt>
                              <dd>{item.device.is_active ? '활성' : '비활성'}</dd>
                            </dl>
                          )}
                          {item.device && (
                            <details className="body-sm mt-3">
                              <summary className="cursor-pointer">
                                장비 식별 정보 · {item.device.device_id.slice(0, 8)}
                              </summary>
                              <dl className="mt-2 grid gap-2 break-all">
                                <div>
                                  <dt>장비 ID</dt>
                                  <dd>{item.device.device_id}</dd>
                                </div>
                                <div>
                                  <dt>Gateway ID</dt>
                                  <dd>{item.device.gateway_id}</dd>
                                </div>
                              </dl>
                            </details>
                          )}
                          {item.device?.core_device_id && propertyId && (
                            <DeviceUsagePanel
                              key={`${String(session.generation)}:${propertyId}:${item.device.core_device_id}`}
                              runtime={runtime}
                              propertyId={propertyId}
                              deviceId={item.device.core_device_id}
                              slots={slotLabels}
                            />
                          )}
                          {canEdit && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  selectPanel(slot.slot_id, 'links');
                                }}
                              >
                                문 연결 편집
                              </Button>
                              {item.device?.core_device_id && (
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    selectPanel(slot.slot_id, 'purpose');
                                  }}
                                >
                                  문 용도 확인·변경
                                </Button>
                              )}
                            </div>
                          )}
                          {canEdit && selectedSlot === slot.slot_id && panel === 'links' && (
                            <SlotDoorEditor
                              key={`${propertyId}:${slot.slot_id}:${String(session.generation)}`}
                              runtime={runtime}
                              propertyId={propertyId}
                              slotId={slot.slot_id}
                              inventoryCursor={cursor}
                              currentDeviceId={item.device?.core_device_id ?? null}
                              label={`${slot.room_name ?? '객실명 없음'} · ${slot.name ?? '슬롯명 없음'}`}
                              onClose={closePanel}
                            />
                          )}
                          {canEdit &&
                            selectedSlot === slot.slot_id &&
                            panel === 'purpose' &&
                            item.device?.core_device_id && (
                              <DevicePurposeEditor
                                key={`${propertyId}:${slot.slot_id}:${String(session.generation)}`}
                                runtime={runtime}
                                id={item.device.core_device_id}
                                propertyId={propertyId}
                                guardNavigation
                              />
                            )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {inventory.isSuccess && !inventory.data.core_slots.length && (
                  <p>조회된 Unit이 없습니다.</p>
                )}
                {inventory.isSuccess &&
                  inventory.data.core_slots.length > 0 &&
                  visibleSlots.length === 0 && (
                    <p>현재 페이지에 조건에 맞는 객실·슬롯이 없습니다.</p>
                  )}
              </>
            )}
            {next && (
              <Button
                disabled={query.isFetching}
                onClick={() => {
                  advance(next);
                }}
              >
                다음 페이지
              </Button>
            )}
            {cursor && (
              <Button
                variant="outline"
                onClick={() => {
                  advance(null);
                }}
              >
                첫 페이지
              </Button>
            )}
          </>
        )}
      </ListPage.Body>
    </ListPage>
  );
}
